import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./RecruitmentSteps.css";
import { GetDashBoardData } from "../../services/jobService";
import { useNavigate } from "react-router-dom";

const containerVariant = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1,
    },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: "easeOut",
    },
  },
};

const RecruitmentSteps = () => {
  const [CountData, SetCountData] = useState({
    AssessmentCount: 0,
    bgv: 0,
    createdJob: 0,
    cvReceived: 0,
    hired: 0,
    hold: 0,
    interviewScheduled: 0,
    publishedJob: 0,
    rejected: 0,
    selected: 0,
    shortlisted: 0,
  });

  const [loading, setLoading] = useState(true);
  const [showSecondRow, setShowSecondRow] = useState(false);
  const [showConnector, setShowConnector] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const GetDashBoardDataCount = async () => {
      try {
        const response = await GetDashBoardData();
        if (response) {
          const Count = response.data.dashboardData[0];
          SetCountData((prevData) => ({ ...prevData, ...Count }));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    GetDashBoardDataCount();
  }, []);

  const steps = [
    {
      count: CountData.createdJob,
      title: "Draft Jobs",
      icon: <i className="bi bi-card-list" />,
      link: "Draft-Jobs",
    },
    {
      count: CountData.publishedJob,
      title: "Live Jobs",
      icon: <i className="bi bi-broadcast" />,
      link: "Live-Jobs",
    },
    {
      count: CountData.cvReceived,
      title: "Applications",
      icon: <i className="bi bi-inbox" />,
      link: "New-Applications",
    },
    {
      count: CountData.shortlisted,
      title: "Shortlisted",
      icon: <i className="bi bi-bookmark-check" />,
      link: "Shortlist-Candidates",
    },
    {
      count: CountData.bgv,
      title: "BGV",
      icon: <i className="bi bi-shield-check" />,
      link: "",
    },
    {
      count: CountData.selected,
      title: "Selected",
      icon: <i className="bi bi-patch-check" />,
      link: "Get-Selected-Candidates",
    },
    {
      count: CountData.interviewScheduled,
      title: "Interviews",
      icon: <i className="bi bi-calendar-event" />,
      link: "Interview-Scheduled",
    },
    {
      count: CountData.AssessmentCount,
      title: "Assessments",
      icon: <i className="bi bi-journal-check" />,
      link: "",
    },
  ];

  return (
    <div className="recruitment-bg mt-5">
      <div className="dashboard-scale">
        <div className="recruitment-steps-wrapper">
          {/*  Show Skeleton Loader Until Data Loads */}
          {loading ? (
            <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
              <div className="recruitment-grid first-row">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="job-card text-center p-3">
                    <Skeleton circle height={60} width={60} className="mb-2" />
                    <Skeleton
                      height={20}
                      width="70%"
                      className="mx-auto mb-2"
                    />
                    <Skeleton height={20} width="40%" className="mx-auto" />
                  </div>
                ))}
              </div>

              <div className="recruitment-grid second-row mt-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i + 4} className="job-card text-center p-3">
                    <Skeleton circle height={60} width={60} className="mb-2" />
                    <Skeleton
                      height={20}
                      width="70%"
                      className="mx-auto mb-2"
                    />
                    <Skeleton height={20} width="40%" className="mx-auto" />
                  </div>
                ))}
              </div>

              <div className="alert alert-warning mt-4 col-6 mx-auto">
                <Skeleton height={20} width="80%" />
                <Skeleton height={15} width="60%" className="mt-2" />
              </div>
            </SkeletonTheme>
          ) : (
            <>
              <>
                {/* First Row */}
                <motion.div
                  className="recruitment-grid first-row"
                  variants={containerVariant}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true }}
                >
                  {steps.slice(0, 4).map((step, i) => (
                    <motion.div
                      key={i}
                      className="job-card"
                      variants={cardVariant}
                      onAnimationComplete={() => {
                        if (i === 3) {
                          setShowSecondRow(true);
                          setShowConnector(true);
                        }
                      }}
                      onClick={() => step.link && navigate(step.link)} // ✅ Navigate without reload
                      style={{
                        cursor: step.link ? "pointer" : "default",
                      }}
                    >
                      <div className="job-card-header">
                        <div className="job-card-icon">{step.icon}</div>
                      </div>
                      <div className="job-card-body text-center">
                        <div className="job-card-title">{step.title}</div>
                        <div className="job-card-count">{step.count}</div>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {/* Second Row */}
                {showSecondRow && (
                  <motion.div
                    className="recruitment-grid second-row"
                    variants={containerVariant}
                    initial="hidden"
                    animate="show"
                  >
                    {steps.slice(4).map((step, i, arr) => (
                      <motion.div
                        key={i + 4}
                        className="job-card"
                        custom={arr.length - i}
                        variants={{
                          hidden: { opacity: 0, y: 40, scale: 0.95 },
                          show: (custom) => ({
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            transition: {
                              duration: 0.7,
                              ease: "easeOut",
                              delay: custom * 0.3,
                            },
                          }),
                        }}
                        onClick={() => step.link && navigate(step.link)} // ✅ SPA navigation
                        style={{
                          cursor: step.link ? "pointer" : "default",
                        }}
                      >
                        <div className="job-card-header">
                          <div className="job-card-icon">{step.icon}</div>
                        </div>
                        <div className="job-card-body text-center">
                          <div className="job-card-title">{step.title}</div>
                          <div className="job-card-count">{step.count}</div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}

                {/* Connector between rows */}
                {showConnector && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc((180px + 110px + 40px) - 20px)",
                      left: "90%",
                      transform: "translateX(-90%)",
                      width: "5px",
                      height: "120px",
                      backgroundColor: "#0d47a1",
                      borderRadius: "4px",
                      zIndex: 2,
                    }}
                  />
                )}

                {/* Summary Card */}
                <div className="row justify-content-end">
                  <div className="alert alert-warning mt-3 col-6">
                    <div className="row">
                      <div className="col-4 fw-bold">Total Candidate</div>
                      <div className="col-6">
                        <div className="row">
                          <div className="col-6">Hired:</div>
                          <div className="col-6">{CountData.hired}</div>
                        </div>
                        <div className="row">
                          <div className="col-6">Hold:</div>
                          <div className="col-6">{CountData.hold}</div>
                        </div>
                        <div className="row">
                          <div className="col-6">Rejected:</div>
                          <div className="col-6">{CountData.rejected}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecruitmentSteps;
